import { Notify } from 'quasar';
import { getCurrentScope as sentryGetCurrentScope } from '@sentry/vue';
import { AuthLoginResponse } from '~/api/auth/_types';
import { useAuthStore } from '~/plugins/auth';
import { AuthUser, UserPreferences } from '~/plugins/auth/_types';
import { t } from '~/plugins/i18n';
import localeStorage from '~/plugins/i18n/store';

// @plachtova: this code cannot be in 'index' and must be in separate file because of CIC dependency
// we need to use this code in api client (for logout) therefore we separated api call and handling result to different files
// so we can call these methods in api client directly

export const finishLogout = async (rememberPath: boolean, silent: boolean = false, noRedirect = false) => {
    const authStore = useAuthStore();

    if (!authStore.loggedIn) return;

    await authStore.logout(rememberPath, noRedirect);

    if (!silent) {
        Notify.create({
            group: 'loggedOut',
            type: 'positive',
            message: t('auth.notify.loggedOut'),
        });
    }

    setTimeout(async () => {
        executePostLogoutActions();
    }, 0);
};

export const finishLogin = async (response: AuthLoginResponse) => {
    const authStore = useAuthStore();
    authStore.setToken({
        ExpiresAt: response.ExpiresAt,
        Token: response.Token,
    });

    await authStore.finishLogin(response.User);

    setPreferencedLocale(response.User.Preferences);

    Notify.create({
        group: 'loggedIn',
        type: 'positive',
        message: t('auth.notify.loggedIn'),
    });

    executePostLoginActions(response.User);
};

export const finishAutologin = (user: AuthUser) => {
    const authStore = useAuthStore();
    authStore.setUser(user);

    setPreferencedLocale(user.Preferences);

    setTimeout(() => {
        executePostLoginActions(user);
    }, 0);
};

export const updateAuthUser = (user: AuthUser) => {
    const authStore = useAuthStore();
    authStore.setUser(user);
};

const executePostLoginActions = async (user: AuthUser) => {
    sentryGetCurrentScope().setUser({ id: user.Id, type: user.Type });
};

const executePostLogoutActions = () => {
    sentryGetCurrentScope().setUser(null);
};

const setPreferencedLocale = async (preferences: UserPreferences | null) => {
    if (preferences?.Locale) {
        localeStorage.activeLocale.value = preferences.Locale;
    }
};
